import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type { AuthRequestConfig } from "~/repository/authSession";
import { normalizeEmailInput } from "~/utils/email";
import type {
  CalendarPreferencesInput,
  MeExtraDataInput,
  MeExtraDataResponse,
  MeInput,
  MeResponse,
  MyStatisticsResponse,
  FeedbackInput,
  FeedbackResponse,
  StudentInput,
  StudentDetailResponse,
  StudentExtraDataBulkItemInput,
  StudentExtraDataInput,
  StudentPatchInput,
  StudentResponse,
  UserPreferencesResponse,
} from "~/types/profile";

class ProfileModule extends HttpFactory {
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance, "teacher");
  }

  async fetchUserData(extras: AuthRequestConfig = {}): Promise<MeResponse> {
    return await this.get(`${ENDPOINTS.PROFILE.ME}`, {}, extras);
  }

  async getMyExtraData(): Promise<MeExtraDataResponse> {
    return await this.get(`${ENDPOINTS.PROFILE.ME_EXTRA_DATA}`);
  }

  async updateMyExtraData(
    extraData: MeExtraDataInput,
  ): Promise<MeExtraDataResponse> {
    return await this.patch(`${ENDPOINTS.PROFILE.ME_EXTRA_DATA}`, extraData);
  }

  async getMyPreferences(): Promise<UserPreferencesResponse> {
    return await this.get(`${ENDPOINTS.PROFILE.ME_PREFERENCES}`);
  }

  async updateMyPreferences(preferences: {
    calendar?: CalendarPreferencesInput;
  }): Promise<UserPreferencesResponse> {
    return await this.patch(`${ENDPOINTS.PROFILE.ME_PREFERENCES}`, preferences);
  }

  async updateUserData(userData: MeInput): Promise<MeResponse> {
    return await this.put(`${ENDPOINTS.PROFILE.ME}`, userData);
  }

  async getMyStatistics(): Promise<MyStatisticsResponse> {
    return await this.get(`${ENDPOINTS.PROFILE.MY_STATISTICS}`);
  }

  async addStudents(studentData: StudentInput[]): Promise<StudentResponse[]> {
    const normalizedStudentData = studentData.map((student) => {
      const normalizedEmail = student.email
        ? normalizeEmailInput(student.email)
        : undefined;

      return {
        ...student,
        email: normalizedEmail || undefined,
      };
    });

    return await this.post(
      `${ENDPOINTS.PROFILE.MY_STUDENTS}`,
      normalizedStudentData,
    );
  }

  async getStudents(includeArchived = false): Promise<StudentResponse[]> {
    const params = includeArchived ? "" : "?include_archived=false";
    return await this.get(`${ENDPOINTS.PROFILE.MY_STUDENTS}${params}`);
  }

  async getStudent(studentId: number): Promise<StudentDetailResponse> {
    return await this.get(`${ENDPOINTS.PROFILE.MY_STUDENTS}${studentId}/`);
  }

  async deleteStudent(studentId: number): Promise<void> {
    return await this.delete(`${ENDPOINTS.PROFILE.MY_STUDENTS}${studentId}/`);
  }

  async archiveStudent(studentId: number): Promise<void> {
    return await this.post(
      `${ENDPOINTS.PROFILE.MY_STUDENTS}${studentId}/archive/`,
      {},
    );
  }

  async unarchiveStudent(studentId: number): Promise<void> {
    return await this.post(
      `${ENDPOINTS.PROFILE.MY_STUDENTS}${studentId}/unarchive/`,
      {},
    );
  }

  async updateStudent(
    studentId: number,
    studentData: StudentPatchInput,
  ): Promise<StudentResponse> {
    return await this.put(
      `${ENDPOINTS.PROFILE.MY_STUDENTS}${studentId}/`,
      studentData,
    );
  }

  async updateStudentExtraData(
    studentId: number,
    extraData: StudentExtraDataInput,
  ): Promise<StudentDetailResponse> {
    return await this.patch(
      `${ENDPOINTS.PROFILE.MY_STUDENTS}${studentId}/extra-data/`,
      extraData,
    );
  }

  async updateStudentsExtraData(
    items: StudentExtraDataBulkItemInput[],
  ): Promise<StudentDetailResponse[]> {
    return await this.patch(
      `${ENDPOINTS.PROFILE.MY_STUDENTS}extra-data/bulk/`,
      items,
    );
  }

  async sendFeedback(feedback: FeedbackInput): Promise<FeedbackResponse> {
    return await this.post<FeedbackResponse>(
      `${ENDPOINTS.PROFILE.FEEDBACKS}`,
      feedback,
    );
  }
}

export default ProfileModule;
